import type {
  AuthUser,
  ForgotPasswordPayload,
  LoginPayload,
  ResetPasswordPayload
} from '~/types/types'
import { useAuthRepository } from '~/repositories/authRepository'

export const useAuth = () => {
  const repository = useAuthRepository()
  const tenant = useTenant()
  const user = useState<AuthUser | null>('auth-user', () => null)
  const isLoading = useState<boolean>('auth-loading', () => false)

  const isLoggedIn = computed(() => Boolean(user.value))
  const isAuthenticated = computed(() => Boolean(user.value && user.value.email_verified_at))

  const init = async () => {
    if (user.value || isLoading.value) {
      return
    }

    isLoading.value = true
    try {
      user.value = await repository.me()
    } catch {
      user.value = null
    } finally {
      isLoading.value = false
    }
  }

  const refresh = async () => {
    try {
      user.value = await repository.me()
    } catch { }
  }

  const login = async (payload: LoginPayload) => {
    isLoading.value = true
    try {
      const result = await repository.login(payload)
      user.value = result.user || await repository.me()
      return { requiresEmailVerification: result.requiresEmailVerification }
    } finally {
      isLoading.value = false
    }
  }

  const register = async (payload: import('~/repositories/authRepository').RegisterPayload) => {
    isLoading.value = true
    try {
      const result = await repository.register(payload)
      user.value = result.user || await repository.me()
      return { requiresEmailVerification: result.requiresEmailVerification, tenant: result.tenant }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    isLoading.value = true
    try {
      await repository.logout()
      user.value = null
    } finally {
      isLoading.value = false
    }
  }

  const forgotPassword = async (payload: ForgotPasswordPayload) => {
    isLoading.value = true
    try {
      await repository.forgotPassword(payload)
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (payload: ResetPasswordPayload) => {
    isLoading.value = true
    try {
      await repository.resetPassword(payload)
    } finally {
      isLoading.value = false
    }
  }

  const resendEmailVerification = async () => {
    isLoading.value = true
    try {
      await repository.resendEmailVerification()
    } finally {
      isLoading.value = false
    }
  }

  const googleLogin = async (referralCode?: string | null) => {
    isLoading.value = true
    try {
      const url = await repository.getGoogleRedirectUrl(referralCode)
      if (import.meta.client) window.location.href = url
    } finally {
      isLoading.value = false
    }
  }

  const handleGoogleCallback = async (token: string) => {
    isLoading.value = true
    try {
      user.value = await repository.handleGoogleCallback(token)
    } finally {
      isLoading.value = false
    }
  }

  const verifyEmailOtp = async (otp: string) => {
    isLoading.value = true
    try {
      const updated = await repository.verifyEmailOtp(otp)
      if (updated) user.value = updated
    } finally {
      isLoading.value = false
    }
  }

  const resendEmailOtp = async () => {
    isLoading.value = true
    try {
      await repository.resendEmailOtp()
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Validate that a redirect target is a same-origin internal path.
   *
   * SECURITY: rejects open-redirect payloads such as:
   *   //evil.com           — protocol-relative URL
   *   /\evil.com           — backslash-prefixed (some browsers normalise it)
   *   javascript:alert(1)  — javascript scheme (does not start with /)
   *   https://evil.com     — absolute URL
   *
   * Only allows paths that begin with a single forward slash followed by a
   * non-slash, non-backslash character (or that are exactly "/").
   */
  const isSafeInternalPath = (path: string): boolean => {
    if (typeof path !== 'string' || path.length === 0) return false
    if (!path.startsWith('/')) return false
    if (path === '/') return true
    // Block //... and /\... (protocol-relative variants)
    if (path.startsWith('//') || path.startsWith('/\\')) return false
    // Block any URL containing a scheme separator
    if (path.includes('://')) return false
    return true
  }

  const getPostLoginRedirect = (explicitRedirect?: string | null) => {
    // "Generic" redirect targets that should NOT override the role-aware
    // default. These are the places auth middleware sends anonymous users
    // when they fumble onto a profile-ish page; honoring them would drag
    // staff onto /me instead of /agent after login.
    const isGenericRedirect = (path: string) =>
      path === '/' || path === '/me' || path === '/me/'

    const explicit = explicitRedirect && isSafeInternalPath(explicitRedirect)
      ? explicitRedirect
      : null

    // ── Tenant subdomain ──────────────────────────────────────────────
    if (tenant.tenantMode.value !== 'platform') {
      const role = tenant.membership.value?.role
      const isStaff = role === 'admin' || role === 'moderator'
      const staffDefault = isStaff ? '/agent' : '/me'

      // Specific deep-link redirect (e.g. /agent/cases/42) wins; generic
      // /me or / fallbacks lose to the role-aware default.
      if (explicit && !isGenericRedirect(explicit)) return explicit
      return staffDefault
    }

    // ── Platform host ─────────────────────────────────────────────────
    // Same rule: deep links win, generic fallbacks defer to the role-aware
    // default (super-admin → /app, everyone else → /me).
    const platformDefault = user.value?.role === 'admin' ? '/app' : '/me'
    if (explicit && !isGenericRedirect(explicit)) return explicit
    return platformDefault
  }

  return {
    user,
    isLoading,
    isAuthenticated,
    isLoggedIn,
    init,
    refresh,
    login,
    register,
    forgotPassword,
    resetPassword,
    resendEmailVerification,
    logout,
    googleLogin,
    handleGoogleCallback,
    verifyEmailOtp,
    resendEmailOtp,
    getPostLoginRedirect
  }
}
